import { TFunction } from 'i18next'

import { organizationSubscriptionPlanTKeys } from '@modules-deprecated/app/organization/enums/organizationSubscriptionPlanTKeys'
import { SubscriptionPlan } from '@modules-deprecated/app/organization/enums/subscriptionPlan'

import { TrackingContext } from '../../../../enums/trackingContext'
import { ModalContent } from '../types/modalContent'

const getRecurringInvoiceContentVariant = (t: TFunction, planName?: string) => ({
  body: t('premium_feature.feature.recurring_invoices.body', { plan: planName }),
  bullets: [
    t('premium_feature.feature.recurring_invoices.bullet_1'),
    t('premium_feature.feature.recurring_invoices.bullet_2'),
    t('premium_feature.feature.recurring_invoices.bullet_3'),
  ],
  cta: t('premium_feature.change_plan_cta', { plan: planName }),
  heading: t('premium_feature.feature.recurring_invoices.heading'),
})

export const getTrackingContextToModalContentVariant = (t: TFunction, planName?: string) =>
  new Map<TrackingContext[], ModalContent>([
    [
      [TrackingContext.InvoiceAttachment],
      {
        body: t('premium_feature.feature.add_files.body', { plan: planName }),
        bullets: [
          t('premium_feature.feature.add_files.bullet_1'),
          t('premium_feature.feature.add_files.bullet_2'),
          t('premium_feature.feature.add_files.bullet_3'),
        ],
        heading: t('premium_feature.feature.add_files.heading'),
      },
    ],
    [
      [
        TrackingContext.BillWidgetUpload,
        TrackingContext.CreateBillUpload,
        TrackingContext.DaybookAttachment,
        TrackingContext.Uploads,
      ],
      {
        heading: t('premium_feature.feature.upload_receipts.heading'),
        body: t('premium_feature.feature.upload_receipts.body'),
      },
    ],
    [
      [TrackingContext.PaymentMethodEditor],
      {
        heading: t('premium_feature.feature.payment_method.heading'),
        body: t('premium_feature.feature.payment_method.body'),
      },
    ],
    [
      [TrackingContext.CreateRecurringInvoice],
      {
        ...getRecurringInvoiceContentVariant(t, planName),
        planOverrides: {
          [SubscriptionPlan.Free]: getRecurringInvoiceContentVariant(
            t,
            t(organizationSubscriptionPlanTKeys[SubscriptionPlan.Plus]),
          ),
          [SubscriptionPlan.CpaFree]: getRecurringInvoiceContentVariant(
            t,
            t(organizationSubscriptionPlanTKeys[SubscriptionPlan.CpaPlus]),
          ),
        },
      },
    ],
    [
      [TrackingContext.SalarySignup],
      {
        heading: t('premium_feature.feature.salary.heading'),
        body: t('premium_feature.feature.salary.body'),
      },
    ],
    [
      [TrackingContext.DaybookIndex, TrackingContext.InvoiceEdit],
      {
        heading: t('premium_feature.feature.more_bookkeeping.heading'),
        body: t('premium_feature.feature.more_bookkeeping.body'),
      },
    ],
    [
      [TrackingContext.OrganizationSettingsUser],
      {
        heading: t('premium_feature.feature.add_users.heading'),
        body: t('premium_feature.feature.add_users.body'),
      },
    ],
    [
      [TrackingContext.BankAccountOverviewConnectBank, TrackingContext.BankConnectionsConnectBank],
      {
        heading: t('premium_feature.feature.reconcile_bank.heading'),
        body: t('premium_feature.feature.reconcile_bank.body'),
      },
    ],
    [
      [TrackingContext.BankTransactions],
      {
        heading: t('premium_feature.feature.bank_transactions.heading'),
        body: t('premium_feature.feature.bank_transactions.body'),
      },
    ],
    [
      [TrackingContext.CreateQuote],
      {
        heading: t('premium_feature.feature.quotes.heading'),
        body: t('premium_feature.feature.quotes.body'),
      },
    ],
    [
      [TrackingContext.EmailRateExceeded],
      {
        heading: t('premium_feature.feature.email_rate_exceeded.heading'),
        body: t('premium_feature.feature.email_rate_exceeded.body'),
      },
    ],
    [
      [TrackingContext.EmailHigherRateExceeded],
      {
        heading: t('premium_feature.feature.email_higher_rate_exceeded.heading'),
        body: t('premium_feature.feature.email_higher_rate_exceeded.body'),
      },
    ],
    [
      [TrackingContext.InvoiceTemplateDesigner],
      {
        heading: t('premium_feature.feature.design_invoices.heading'),
        body: t('premium_feature.feature.design_invoices.body'),
      },
    ],
    [
      [TrackingContext.Integrations],
      {
        heading: t('premium_feature.feature.integrations.heading'),
        body: t('premium_feature.feature.integrations.body'),
      },
    ],
    [
      [TrackingContext.EInvoice],
      {
        heading: t('premium_feature.feature.e_invoice.heading'),
        body: t('premium_feature.feature.e_invoice.body'),
      },
    ],
    [
      [TrackingContext.FreeInvoicesLimitExceeded],
      {
        heading: t('premium_feature.feature.free_invoices_limit.heading'),
        body: t('premium_feature.feature.free_invoices_limit.body'),
      },
    ],
    [
      [TrackingContext.FreeExpensesLimitExceeded],
      {
        heading: t('premium_feature.feature.free_expenses_limit.heading'),
        body: t('premium_feature.feature.free_expenses_limit.body', { limit: 10 }),
      },
    ],
    [
      [TrackingContext.Scout],
      {
        heading: t('premium_feature.feature.scout.heading'),
        body: t('premium_feature.feature.scout.body'),
      },
    ],
    [
      [TrackingContext.InvoiceDesigner],
      {
        heading: t('premium_feature.feature.invoice_designer.heading'),
        body: t('premium_feature.feature.invoice_designer.body'),
      },
    ],
    [
      [TrackingContext.InvoiceMultiCurrency],
      {
        body: t('premium_feature.feature.invoice_multi_currency.body', { plan: planName }),
        bullets: [
          t('premium_feature.feature.invoice_multi_currency.bullet_1'),
          t('premium_feature.feature.invoice_multi_currency.bullet_2'),
          t('premium_feature.feature.invoice_multi_currency.bullet_3'),
        ],
        cta: t('premium_feature.change_plan_cta', { plan: planName }),
        heading: t('premium_feature.feature.invoice_multi_currency.heading'),
      },
    ],
    [
      [TrackingContext.CreateCustomer],
      {
        heading: t('premium_feature.feature.client_limit.heading'),
        body: t('premium_feature.feature.client_limit.body'),
        bullets: [
          t('premium_feature.feature.client_limit.bullet_1'),
          t('premium_feature.feature.client_limit.bullet_2'),
        ],
        cta: t('premium_feature.feature.client_limit.cta'),
        billyOverrides: {
          heading: t('premium_feature.feature.create_company_customer.heading'),
          body: t('premium_feature.feature.create_company_customer.body'),
        },
        planOverrides: {
          [SubscriptionPlan.Free]: {
            heading: t('premium_feature.feature.client_limit.free.heading'),
            body: t('premium_feature.feature.client_limit.free.body'),
            bullets: [
              t('premium_feature.feature.client_limit.free.bullet_1'),
              t('premium_feature.feature.client_limit.free.bullet_2'),
              t('premium_feature.feature.client_limit.free.bullet_3'),
            ],
            cta: t('premium_feature.feature.client_limit.free.cta'),
          },
        },
      },
    ],
    [
      [TrackingContext.QuoteConvertToInvoice],
      {
        body: t('premium_feature.feature.quote_convert_to_invoice.body', { plan: planName }),
        bullets: [
          t('premium_feature.feature.quote_convert_to_invoice.bullet_1'),
          t('premium_feature.feature.quote_convert_to_invoice.bullet_2'),
          t('premium_feature.feature.quote_convert_to_invoice.bullet_3'),
        ],
        heading: t('premium_feature.feature.quote_convert_to_invoice.heading', { plan: planName }),
      },
    ],
    [
      [TrackingContext.ShineEInvoicing],
      {
        heading: t('premium_feature.feature.shine_e_invoicing.heading'),
        body: t('premium_feature.feature.shine_e_invoicing.body'),
      },
    ],
    [
      [TrackingContext.Attachments],
      {
        heading: t('premium_feature.feature.attachments.heading'),
        body: t('premium_feature.feature.attachments.body'),
      },
    ],
    [
      [TrackingContext.BankConnectionLimitsExceeded],
      {
        heading: t('premium_feature.feature.more_bank_connections.heading'),
        body: t('premium_feature.feature.more_bank_connections.body'),
      },
    ],
    [
      [TrackingContext.InvoiceDiscount],
      {
        heading: t('premium_feature.feature.discounts.heading', { plan: planName }),
        body: t('premium_feature.feature.discounts.body'),
      },
    ],
    [
      [
        TrackingContext.DepositInvoiceUpsellQuotesList,
        TrackingContext.DepositInvoiceUpsellInvoicesList,
        TrackingContext.DepositInvoiceUpsellQuoteDetails,
      ],
      {
        heading: t('premium_feature.feature.deposit_invoice_upsell.heading'),
        body: t('premium_feature.feature.deposit_invoice_upsell.body'),
        bullets: [
          t('premium_feature.feature.deposit_invoice_upsell.bullet_1'),
          t('premium_feature.feature.deposit_invoice_upsell.bullet_2'),
          t('premium_feature.feature.deposit_invoice_upsell.bullet_3'),
        ],
      },
    ],
  ])
